import getOption from 'utils/helpers/getOption';
import { FormElementPropsType } from '../../../types/stepFlow.type';

// GPS types with their uncertainty mappings
export const GPS_TYPES: Record<
  string,
  { label: string; accuracy: number; units: string }
> = {
  WAAS: {
    label: '2 m: WAAS (Wide Area Augmentation System)',
    accuracy: 2,
    units: 'm',
  },
  LADGPS: {
    label:
      '5 to 15 cm: Local Area Differential GPS, John Deere StarFire 1 (SF1)',
    accuracy: 15,
    units: 'cm',
  },
  RTK: {
    label:
      '5 cm or less: Real-Time Kinematic (RTK), Network RTK, John Deere StarFire 2/3/RTK (SF2/SF3/SF-RTK) ',
    accuracy: 5,
    units: 'cm',
  },
};

export const getEquipmentFormElements = (
  mode: 'execution' | 'harvester',
  equipmentType?: string,
): FormElementPropsType[] => {
  const isExecution = mode === 'execution';
  const equipmentName = isExecution
    ? equipmentType || 'Equipment'
    : 'Harvester';

  // GPS type options for dropdown
  const gpsTypeOptions = Object.entries(GPS_TYPES).map(([value, config]) =>
    getOption(value, config.label),
  );

  const unitOptions = [getOption('ft', 'ft'), getOption('m', 'm')];

  const accuracyUnitOptions = [
    getOption('m', 'm'),
    getOption('cm', 'cm'),
    getOption('ft', 'ft'),
    getOption('in', 'in'),
  ];

  // Dynamic field names based on mode
  const swathWidthField = isExecution
    ? 'execution_equipment_swath_width'
    : 'harvester_swath_width';
  const swathWidthUnitsField = isExecution
    ? 'execution_equipment_swath_width_units'
    : 'harvester_swath_width_units';
  const dgpsTypeField = isExecution
    ? 'execution_equipment_dgps_type'
    : 'harvester_dgps_type';
  const dgpsAccuracyField = isExecution
    ? 'execution_equipment_dgps_accuracy'
    : 'harvester_dgps_accuracy';
  const dgpsUnitsField = isExecution
    ? 'execution_equipment_dgps_units'
    : 'harvester_dgps_units';

  const commonElements: FormElementPropsType[] = [
    // Swath Width
    {
      element: 'horizontalInputPair',
      elements: [
        {
          name: swathWidthField,
          label: `${equipmentName} swath width:`,
          tooltip: `How wide is your ${equipmentName.toLowerCase()}?`,
          type: 'number',
          step: '0.01',
        },
        {
          element: 'radioGroup',
          name: swathWidthUnitsField,
          radioGroupHeader: 'Units:',
          options: unitOptions,
          value: 'ft',
        },
      ],
    },
    // GPS Type - updated labels to include equipment type
    {
      element: 'horizontalSingleInput',
      inputElement: {
        element: 'select',
        name: dgpsTypeField,
        label: `${equipmentName} GPS uncertainty and type:`,
        options: gpsTypeOptions,
        allowOther: true,
        otherInputLabel: 'Other GPS type:',
        tooltip:
          "Select the level of GPS uncertainty this equipment has, using the types of GPS systems listed as guidelines. If you're not sure, select WAAS.",
      },
    },
    // GPS Accuracy - only show when "Other" is selected
    {
      element: 'horizontalInputPair',
      condition: {
        field: dgpsTypeField,
        operator: 'not-in',
        values: Object.keys(GPS_TYPES),
      },
      elements: [
        {
          name: dgpsAccuracyField,
          label: 'GPS uncertainty:',
          type: 'number',
          step: '1',
        },
        {
          element: 'radioGroup',
          name: dgpsUnitsField,
          radioGroupHeader: 'Units:',
          options: accuracyUnitOptions,
          value: 'm',
        },
      ],
    },
  ];

  if (isExecution) {
    return [
      ...commonElements,
      // Rate Change Distance
      {
        element: 'horizontalInputPair',
        elements: [
          {
            name: 'execution_equipment_rate_change_distance',
            label: `${equipmentName} rate change distance:`,
            tooltip: `What is the distance your ${equipmentName.toLowerCase()} will travel while changing rates?`,
            type: 'number',
            step: '1',
          },
          {
            element: 'radioGroup',
            name: 'execution_equipment_rate_change_distance_units',
            radioGroupHeader: 'Units:',
            options: unitOptions,
            value: 'ft',
          },
        ],
      },
      // Can Split Swath
      {
        element: 'horizontalSingleInput',
        inputElement: {
          element: 'checkbox',
          name: 'execution_equipment_can_split_swath',
          label: `Can the ${equipmentName.toLowerCase()} split its swath in half?`,
          tooltip: `If your ${equipmentName.toLowerCase()} can apply one treatment or rate to the left half of the machine and another to the right half, check this box.`,
        },
      },
      // Treatment Drift Section
      {
        element: 'sectionHeading',
        label: 'Treatment Drift',
        tooltip:
          'How far could the treatment drift (due to wind, migration/drainage, etc) from the area it is applied?',
      },
      // Treatment Drift Distance - collapsed to single field
      {
        element: 'horizontalInputPair',
        elements: [
          {
            name: 'treatment_drift_distance',
            label: 'Treatment drift distance:',
            type: 'number',
            step: '1',
            tooltip:
              'Maximum distance treatment could drift from application area',
          },
          {
            element: 'radioGroup',
            name: 'treatment_drift_units',
            radioGroupHeader: 'Units:',
            options: unitOptions,
            value: 'ft',
          },
        ],
      },
    ];
  } else {
    return [
      ...commonElements,
      // Spatial Resolution
      {
        element: 'horizontalInputPair',
        elements: [
          {
            name: 'harvester_spatial_resolution',
            label: 'Spatial resolution of yield monitor:',
            type: 'number',
            step: '1',
            tooltip:
              "This is the distance along the direction of travel that each datapoint from your harvester's yield monitor represents/aggregates from.",
          },
          {
            element: 'radioGroup',
            name: 'harvester_spatial_resolution_units',
            radioGroupHeader: 'Units:',
            options: unitOptions,
            value: 'ft',
          },
        ],
      },
      // Max Harvest Angle
      {
        element: 'horizontalSingleInput',
        inputElement: {
          name: 'max_likely_harvest_angle',
          label: 'Maximum likely harvest angle:',
          type: 'number',
          step: '1',
          rightElement: 'deg',
          tooltip:
            'If you plan to harvest at an angle (e.g. for soybeans), this is the maximum angle you expect to use, in degrees away from the planting direction.',
        },
      },
    ];
  }
};
